import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Printer, 
  ClipboardList,
  Package,
  Loader2,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { parseLocation, formatLocation } from "@/components/LocationInput";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Pull the full product catalogue via the paginated endpoint so stock-take
// sheets never silently truncate when the catalogue grows past the server-
// side cap on `/api/products?limit=` (5000). `/api/products/paginated`
// caps `limit` at 200 per page.
const fetchAllProducts = async () => {
  const out = [];
  const PAGE = 200;
  const SAFETY_CAP = 50000;
  let skip = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await axios.get(`${API}/products/paginated`, {
      params: { limit: PAGE, skip },
    });
    const rows = res.data?.products || [];
    out.push(...rows);
    const hasMore = !!res.data?.has_more;
    if (!hasMore || rows.length === 0 || out.length >= SAFETY_CAP) break;
    skip += PAGE;
  }
  return out;
};

export const PrintStocktakeSheets = () => {
  const navigate = useNavigate();
  const printRef = useRef();
  
  // Filter options state
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shelfLocations, setShelfLocations] = useState([]);
  
  // Selected filters
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedShelf, setSelectedShelf] = useState("");
  const [selectedShelfStart, setSelectedShelfStart] = useState("");
  const [selectedShelfEnd, setSelectedShelfEnd] = useState("");

  
  // Print options
  const [showSOH, setShowSOH] = useState(true);
  const [sortByCategory, setSortByCategory] = useState(false);
  const [excludeInactive, setExcludeInactive] = useState(true);
  const [excludeZeroStock, setExcludeZeroStock] = useState(false);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [generatedSheets, setGeneratedSheets] = useState([]);

  
  // Products to print
  const [products, setProducts] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    setLoadingData(true);
    try {
      // Fetch suppliers
      const suppliersRes = await axios.get(`${API}/suppliers`);
      // Sort suppliers alphabetically by name
      const sortedSuppliers = (suppliersRes.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setSuppliers(sortedSuppliers);
      
      // Fetch categories/departments from settings
      const catDeptRes = await axios.get(`${API}/categories-departments`);
      const catDeptData = catDeptRes.data || {};
      
      // Extract categories and departments from the response (sorted alphabetically)
      const cats = (catDeptData.categories || []).map(c => c.name).sort((a, b) => a.localeCompare(b));
      const depts = (catDeptData.departments || []).map(d => d.name).sort((a, b) => a.localeCompare(b));
      setCategories(cats);
      setDepartments(depts);
      
      // Fetch shelf locations directly from the dedicated endpoint
      const shelfBinsRes = await axios.get(`${API}/products/shelf-bins`);
      const shelves = shelfBinsRes.data || [];
      
      console.log('Shelf locations from API:', shelves);
      setShelfLocations(shelves);
      
    } catch (error) {
      console.error("Error fetching filter options:", error);
      toast.error("Failed to load filter options");
    } finally {
      setLoadingData(false);
    }
  };

  const parseShelfCode = (shelfCode) => {
    const normalized = (shelfCode || "").trim().toUpperCase();
    
    // Match formats like: IO-1, IO-2 (letters only, dash, number)
    // OR: I0-1, A1-2, B10-5 (letters, number, dash, number)
    // OR: simple A1, B2 (letters, number)
    
    // First try: letters + optional number + dash + number (e.g., IO-1, I0-1, A1-2)
    let match = normalized.match(/^([A-Z]+)(\d*)(?:-(\d+))$/);
    if (match) {
      return {
        raw: normalized,
        prefix: match[1],
        number: match[2] ? parseInt(match[2], 10) : 0,  // Default to 0 if no number
        suffix: parseInt(match[3], 10),
        suffixStr: match[3]
      };
    }
    
    // Second try: letters + number (e.g., A1, B2, I0)
    match = normalized.match(/^([A-Z]+)(\d+)$/);
    if (match) {
      return {
        raw: normalized,
        prefix: match[1],
        number: parseInt(match[2], 10),
        suffix: null,
        suffixStr: ""
      };
    }
    
    return null;
  };

  const getProductShelfCode = (product) => {
    if (product.location) {
      const { shelf } = parseLocation(product.location);
      return shelf?.toUpperCase() || null;
    }
    // For legacy shelf_number field
    return product.shelf_number?.toUpperCase() || null;
  };

  const sortProductsForShelf = (prods) => {
    return [...prods].sort((a, b) => {
      const locA = parseLocation(a.location || formatLocation(a.shelf_number, a.bin_number));
      const locB = parseLocation(b.location || formatLocation(b.shelf_number, b.bin_number));
      const binNumA = parseInt(locA.bin || "0", 10);
      const binNumB = parseInt(locB.bin || "0", 10);
      return binNumA - binNumB;
    });
  };

  const fetchProducts = async (filterType, filterValue) => {
    setLoading(true);
    try {
      // Pull the full catalogue (paginated, no truncation) then filter client-side.
      const allProducts = await fetchAllProducts();
      let prods = allProducts;
      
      // Apply filters based on type
      if (filterType === "supplier" && filterValue) {
        // Find the supplier name from the ID
        const supplier = suppliers.find(s => s.id === filterValue);
        const supplierName = supplier?.name || "";
        // Filter by supplier_id OR supplier name text field
        prods = prods.filter(p => 
          p.preferred_supplier_id === filterValue || 
          p.supplier_id === filterValue ||
          (p.supplier && p.supplier.toUpperCase() === supplierName.toUpperCase())
        );
      } else if (filterType === "category" && filterValue) {
        prods = prods.filter(p => p.category === filterValue);
      } else if (filterType === "department" && filterValue) {
        prods = prods.filter(p => p.department === filterValue);
      } else if (filterType === "shelf" && filterValue) {
        // Filter by shelf code (match locations that start with the shelf)
        prods = prods.filter(p => {
          // Check the new location field first
          if (p.location) {
            const { shelf } = parseLocation(p.location);
            return shelf === filterValue;
          }
          // Also check legacy shelf_number format
          return p.shelf_number?.toUpperCase() === filterValue;
        });
      }
      
      // Apply additional filters
      if (excludeInactive) {
        prods = prods.filter(p => p.is_active !== false);
      }
      if (excludeZeroStock) {
        prods = prods.filter(p => (p.current_stock || 0) > 0);
      }
      
      // Sort - by bin number when filtering by shelf, otherwise by category or name
      if (filterType === "shelf") {
        // Sort by bin number numerically when filtering by shelf
        prods.sort((a, b) => {
          const { bin: binA } = parseLocation(a.location || formatLocation(a.shelf_number, a.bin_number));
          const { bin: binB } = parseLocation(b.location || formatLocation(b.shelf_number, b.bin_number));
          // Compare bin numbers numerically
          const binNumA = parseInt(binA || '0', 10);
          const binNumB = parseInt(binB || '0', 10);
          return binNumA - binNumB;
        });
      } else if (sortByCategory) {
        prods.sort((a, b) => {
          const catCompare = (a.category || "").localeCompare(b.category || "");
          if (catCompare !== 0) return catCompare;
          return (a.name || "").localeCompare(b.name || "");
        });
      } else {
        prods.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      
      const filterDescription = getFilterDescription(filterType, filterValue);
      setGeneratedSheets([
        {
          key: `${filterType || "all"}-${filterValue || "all"}`,
          title: "STOCK TAKE SHEET",
          description: filterDescription,
          products: prods
        }
      ]);

      setProducts(prods);
      setShowPreview(true);
      
      if (prods.length === 0) {
        toast.info("No products found matching the criteria");
      } else {
        toast.success(`Found ${prods.length} products`);
      }
      
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintAll = () => {
    fetchProducts("all", null);
  };

  const handlePrintBySupplier = () => {
    if (!selectedSupplier) {
      toast.error("Please select a supplier");
      return;
    }
    fetchProducts("supplier", selectedSupplier);
  };

  const handlePrintByCategory = () => {
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    fetchProducts("category", selectedCategory);
  };

  const handlePrintByDepartment = () => {
    if (!selectedDepartment) {
      toast.error("Please select a department");
      return;
    }
    fetchProducts("department", selectedDepartment);
  };

  const handlePrintByShelf = () => {
    if (!selectedShelf) {
      toast.error("Please select a shelf");
      return;
    }
    fetchProducts("shelf", selectedShelf);
  };

  const handlePrintByShelfRange = async () => {
    if (!selectedShelfStart || !selectedShelfEnd) {
      toast.error("Please select both start and end shelves");
      return;
    }

    const start = parseShelfCode(selectedShelfStart);
    const end = parseShelfCode(selectedShelfEnd);

    if (!start || !end) {
      toast.error("Invalid shelf range format");
      return;
    }

    if (start.prefix !== end.prefix) {
      toast.error("Range must use the same shelf prefix");
      return;
    }

    const minNumber = Math.min(start.number, end.number);
    const maxNumber = Math.max(start.number, end.number);
    
    // Handle suffix-based range (e.g., I0-1 to I0-2)
    const hasSuffixRange = start.suffix !== null && end.suffix !== null;
    const minSuffix = hasSuffixRange ? Math.min(start.suffix, end.suffix) : null;
    const maxSuffix = hasSuffixRange ? Math.max(start.suffix, end.suffix) : null;

    const shelvesInRange = shelfLocations
      .map((shelf) => ({ shelf, parsed: parseShelfCode(shelf) }))
      .filter(({ parsed }) => {
        if (!parsed || parsed.prefix !== start.prefix) return false;
        
        // If both start and end have the same main number (e.g., I0-1 to I0-2)
        // Then filter by suffix range
        if (start.number === end.number && hasSuffixRange) {
          if (parsed.number !== start.number) return false;
          if (parsed.suffix === null) return false;
          return parsed.suffix >= minSuffix && parsed.suffix <= maxSuffix;
        }
        
        // Otherwise filter by main number range
        if (parsed.number < minNumber || parsed.number > maxNumber) return false;
        
        return true;
      })
      .sort((a, b) => {
        // Sort by number first, then by suffix
        if (a.parsed.number !== b.parsed.number) {
          return a.parsed.number - b.parsed.number;
        }
        return (a.parsed.suffix || 0) - (b.parsed.suffix || 0);
      })
      .map(({ shelf }) => shelf);

    if (shelvesInRange.length === 0) {
      toast.info("No shelves found in selected range");
      return;
    }

    setLoading(true);
    try {
      let allProducts = await fetchAllProducts();

      if (excludeInactive) {
        allProducts = allProducts.filter(p => p.is_active !== false);
      }
      if (excludeZeroStock) {
        allProducts = allProducts.filter(p => (p.current_stock || 0) > 0);
      }

      const sheets = shelvesInRange.map((shelf) => {
        const shelfProducts = allProducts.filter((p) => {
          const productShelf = getProductShelfCode(p);
          if (!productShelf) return false;
          const parsed = parseShelfCode(productShelf);
          return parsed && parsed.raw === shelf.toUpperCase();
        });

        return {
          key: `shelf-${shelf}`,
          title: "STOCK TAKE SHEET",
          description: `Shelf: ${shelf}`,
          products: sortProductsForShelf(shelfProducts)
        };
      }).filter((sheet) => sheet.products.length > 0);

      if (sheets.length === 0) {
        toast.info("No products found in selected shelf range");
        return;
      }

      setGeneratedSheets(sheets);
      setProducts(sheets.flatMap((sheet) => sheet.products));
      setShowPreview(true);
      toast.success(`Generated ${sheets.length} shelf sheets`);
    } catch (error) {
      console.error("Error fetching products for shelf range:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    const styles = `
      <style>
        /* Extra bottom margin (18mm) reserves headroom for any browser
         * header/footer the user might have enabled in their print dialog
         * (Chrome default adds ~15mm). Was 10mm on every side — that
         * squeezed the last row against the page edge and it got clipped. */
        @page { size: A4; margin: 12mm 10mm 18mm 10mm; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: 10px;
          margin: 0;
          padding: 0;
        }
        .sheet {
          page-break-after: always;
          break-after: page;
          /* Reserve a few extra mm at the bottom of every sheet so the last
           * row + signature block are never cropped by the printer's dead
           * zone. */
          padding-bottom: 6mm;
        }
        .sheet:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        .header {
          text-align: center;
          margin-bottom: 10px;
          border-bottom: 2px solid #000;
          padding-bottom: 5px;
        }
        .header h1 { 
          font-size: 18px; 
          margin: 0 0 5px 0;
        }
        .header .info {
          font-size: 11px;
          color: #333;
        }
        table { 
          width: 100%; 
          border-collapse: collapse;
          margin-top: 10px;
        }
        th, td { 
          border: 1px solid #333; 
          padding: 4px 6px;
          text-align: left;
        }
        th { 
          background-color: #f0f0f0;
          font-weight: bold;
          font-size: 10px;
        }
        td { font-size: 9px; }
        tr { page-break-inside: avoid; break-inside: avoid; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        .plu { width: 60px; font-family: monospace; }
        .name { width: auto; }
        .category { width: 80px; font-size: 8px; }
        .bin { width: 50px; text-align: center; }
        .soh { width: 50px; text-align: right; }
        .count-box { 
          width: 60px; 
          text-align: center;
        }
        .count-box-inner {
          border: 2px solid #000;
          min-height: 20px;
          background: #fff;
        }
        .footer {
          margin-top: 20px;
          border-top: 1px solid #333;
          padding-top: 10px;
          font-size: 9px;
          /* Keep the signature block together and force it to travel with
           * the last table row instead of leaving an orphan row behind. */
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .signature-line {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
        }
        .signature-box {
          width: 200px;
          border-top: 1px solid #000;
          padding-top: 5px;
          text-align: center;
        }
        @media print {
          body { -webkit-print-color-adjust: exact; }
        }
      </style>
    `;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Stock Take Sheet</title>
          ${styles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const getFilterDescription = (filterType = null, filterValue = null) => {
    if (filterType === "shelf") return `Shelf: ${filterValue}`;
    if (filterType === "category") return `Category: ${filterValue}`;
    if (filterType === "department") return `Department: ${filterValue}`;
    if (filterType === "supplier") {
      const supplier = suppliers.find(s => s.id === filterValue);
      return `Supplier: ${supplier?.name || filterValue}`;
    }
    if (filterType === "shelf_range") return filterValue;
    if (filterType === "all") return "All Products";

    if (selectedShelf) return `Shelf: ${selectedShelf}`;
    if (selectedCategory) return `Category: ${selectedCategory}`;
    if (selectedDepartment) return `Department: ${selectedDepartment}`;
    if (selectedSupplier) {
      const supplier = suppliers.find(s => s.id === selectedSupplier);
      return `Supplier: ${supplier?.name || selectedSupplier}`;
    }
    return "All Products";
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold">Print Stocktake Sheets</h1>
            <p className="text-gray-500">Generate printable stock counting sheets</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate("/stock-take")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Stock Take
        </Button>
      </div>


              {/* By Shelf Range */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid="stocktake-shelf-range-row">
                <span className="font-medium whitespace-nowrap">Shelf Range:</span>
                <Select value={selectedShelfStart} onValueChange={setSelectedShelfStart}>
                  <SelectTrigger className="flex-1" data-testid="stocktake-shelf-range-start-select">
                    <SelectValue placeholder="Start shelf..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...shelfLocations].sort((a, b) => a.localeCompare(b)).map((shelf) => (
                      <SelectItem key={`start-${shelf}`} value={shelf}>
                        {shelf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-500">to</span>
                <Select value={selectedShelfEnd} onValueChange={setSelectedShelfEnd}>
                  <SelectTrigger className="flex-1" data-testid="stocktake-shelf-range-end-select">
                    <SelectValue placeholder="End shelf..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...shelfLocations].sort((a, b) => a.localeCompare(b)).map((shelf) => (
                      <SelectItem key={`end-${shelf}`} value={shelf}>
                        {shelf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handlePrintByShelfRange}
                  disabled={loading || !selectedShelfStart || !selectedShelfEnd}
                  data-testid="stocktake-print-range-button"
                >
                  PRINT RANGE
                </Button>
              </div>

      {!showPreview ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Options Card */}
          <Card>
            <CardHeader>
              <CardTitle>Print Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="showSOH" 
                  checked={showSOH}
                  onCheckedChange={setShowSOH}
                />
                <Label htmlFor="showSOH">Show Stock on Hand (SOH)</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sortByCategory" 
                  checked={sortByCategory}
                  onCheckedChange={setSortByCategory}
                />
                <Label htmlFor="sortByCategory">Sort by category, then alphabetically</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="excludeInactive" 
                  checked={excludeInactive}
                  onCheckedChange={setExcludeInactive}
                />
                <Label htmlFor="excludeInactive">Exclude inactive products</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="excludeZeroStock" 
                  checked={excludeZeroStock}
                  onCheckedChange={setExcludeZeroStock}
                />
                <Label htmlFor="excludeZeroStock">Exclude zero stock items</Label>
              </div>
            </CardContent>
          </Card>

          {/* Print Options Card */}
          <Card>
            <CardHeader>
              <CardTitle>Generate Sheets</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Print All */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium">Print Sheets for ALL Stock</span>
                <Button onClick={handlePrintAll} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                  PRINT
                </Button>
              </div>

              {/* By Supplier */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="font-medium whitespace-nowrap">By Supplier:</span>
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select supplier..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...suppliers].sort((a, b) => ((a.name || a.id || '') + '').localeCompare((b.name || b.id || '') + '')).map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handlePrintBySupplier} disabled={loading || !selectedSupplier}>
                  PRINT
                </Button>
              </div>

              {/* By Category */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="font-medium whitespace-nowrap">By Category:</span>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...categories].sort((a, b) => (a || '').localeCompare(b || '')).map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handlePrintByCategory} disabled={loading || !selectedCategory}>
                  PRINT
                </Button>
              </div>

              {/* By Department */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="font-medium whitespace-nowrap">By Department:</span>
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select department..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...departments].sort((a, b) => (a || '').localeCompare(b || '')).map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handlePrintByDepartment} disabled={loading || !selectedDepartment}>
                  PRINT
                </Button>
              </div>

              {/* By Shelf Location */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="font-medium whitespace-nowrap">By Shelf:</span>
                <Select value={selectedShelf} onValueChange={setSelectedShelf}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select shelf..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...shelfLocations].sort((a, b) => a.localeCompare(b)).map((shelf) => (
                      <SelectItem key={shelf} value={shelf}>
                        {shelf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handlePrintByShelf} disabled={loading || !selectedShelf}>
                  PRINT
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Preview Section */
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold">Preview - {generatedSheets.length} Sheet{generatedSheets.length === 1 ? '' : 's'} ({products.length} Products)</h2>
              <p className="text-gray-500">{generatedSheets[0]?.description || getFilterDescription()}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button onClick={handlePrint} className="bg-purple-600 hover:bg-purple-700">
                <Printer className="h-4 w-4 mr-2" />
                Print Sheet
              </Button>
            </div>
          </div>

          {/* Print Preview */}
          <Card className="p-4 bg-white">
            <div ref={printRef}>
              {generatedSheets.map((sheet, sheetIndex) => (
                <div
                  key={sheet.key || sheetIndex}
                  className="sheet"
                  style={{ marginBottom: sheetIndex < generatedSheets.length - 1 ? '20px' : '0' }}
                  data-testid={`stocktake-sheet-preview-${sheetIndex}`}
                >
                  <div className="header">
                    <h1 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '5px'}}>STOCK TAKE SHEET</h1>
                    <div className="info" style={{fontSize: '11px', color: '#333'}}>
                      <strong>{sheet.description}</strong> |
                      Date: {new Date().toLocaleDateString()} |
                      Total Items: {sheet.products.length}
                    </div>
                  </div>

                  <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '9px'}}>
                    <thead>
                      <tr style={{backgroundColor: '#f0f0f0'}}>
                        <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'left', width: '60px', fontFamily: 'monospace'}}>PLU</th>
                        <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'left', width: '110px', fontFamily: 'monospace'}}>Barcode</th>
                        <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'left'}}>Product Name</th>
                        {sortByCategory && <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'left', width: '80px', fontSize: '8px'}}>Category</th>}
                        <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'center', width: '50px'}}>Shelf</th>
                        {showSOH && <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'right', width: '50px'}}>SOH</th>}
                        <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'center', width: '70px'}}>Count</th>
                        <th style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'center', width: '70px'}}>Recount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sheet.products.map((product, index) => (
                        <tr key={product.id || `${sheet.key}-${index}`}>
                          <td style={{border: '1px solid #333', padding: '4px 6px', fontFamily: 'monospace'}}>{product.plu_code}</td>
                          <td style={{border: '1px solid #333', padding: '4px 6px', fontFamily: 'monospace', fontSize: '8px'}}>{product.barcode || product.barcodes?.[0] || "-"}</td>
                          <td style={{border: '1px solid #333', padding: '4px 6px'}}>{product.name}</td>
                          {sortByCategory && <td style={{border: '1px solid #333', padding: '4px 6px', fontSize: '8px'}}>{product.category || "-"}</td>}
                          <td style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'center', fontFamily: 'monospace'}}>{product.location || formatLocation(product.shelf_number, product.bin_number) || "-"}</td>
                          {showSOH && <td style={{border: '1px solid #333', padding: '4px 6px', textAlign: 'right'}}>{(product.current_stock || 0).toFixed(0)}</td>}
                          <td style={{border: '1px solid #333', padding: '2px'}}>
                            <div style={{border: '2px solid #000', minHeight: '22px', backgroundColor: '#fff'}}></div>
                          </td>
                          <td style={{border: '1px solid #333', padding: '2px'}}>
                            <div style={{border: '2px solid #000', minHeight: '22px', backgroundColor: '#fff'}}></div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="footer" style={{marginTop: '20px', borderTop: '1px solid #333', paddingTop: '10px', fontSize: '9px'}}>
                    <div style={{marginTop: '30px', display: 'flex', justifyContent: 'space-between'}}>
                      <div style={{width: '200px', borderTop: '1px solid #000', paddingTop: '5px', textAlign: 'center'}}>
                        Counted By
                      </div>
                      <div style={{width: '200px', borderTop: '1px solid #000', paddingTop: '5px', textAlign: 'center'}}>
                        Checked By
                      </div>
                      <div style={{width: '200px', borderTop: '1px solid #000', paddingTop: '5px', textAlign: 'center'}}>
                        Date
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrintStocktakeSheets;
